<script>
  import { onMount } from 'svelte'

  import dayjs from 'dayjs'
  import relativeTime from 'dayjs/plugin/relativeTime'
  dayjs.extend(relativeTime)

  import { markdown } from '../misc.js'

  import authStore from '../auth/store.js'
  import approxNowStore from '../stores/approx-now.js'
  import userStore from '../stores/user.js'

  import toastStore from './toast/store.js'

  import DialogMenu from './widgets/dialog-menu.svelte'
  import MoreMenu from './widgets/more-menu.svelte'
  import AlertButton from './widgets/alert-button.svelte'
  import StarButton from './widgets/star-button.svelte'

  import PageMain from './page-main.svelte'
  import QueueEdit from './queue-edit.svelte'
  import TaskAdd from './task-add.svelte'
  import TaskEdit from './task-edit.svelte'
  import TaskList from './task-list.svelte'


  export let queueId

  let subView = false
  const closeSubView = function () {
    subView = false
  }
  const editQueue = function () {
    subView = 'queueEdit'
  }
  const queueUpdated = function () {
    userStore.refresh()
      .then(() => {
        subView = false
      })
  }
  const addTask = function () {
    subView = 'taskAdd'
  }
  const taskAdded = function () {
    resetTaskState()
    subView = false
    // Refresh "now" to make sure the new task doesn't look like it was added in the future.
    approxNowStore.refresh()
    loadTask()
  }
  const editTask = function () {
    subView = 'taskEdit'
  }
  const taskUpdated = function () {
    resetTaskState()
    subView = false
    loadTask()
  }

  let listMode = false
  const listTasks = function () {
    listMode = 'current'
  }
  const listOldTasks = function () {
    listMode = 'old'
  }
  const closeList = function () {
    if (listMode === 'current') {
      // Tasks can be changed in the current list view. e.g. starred.
      resetTaskState()
      listMode = false
      loadTask()
    } else {
      listMode = false
    }
  }

  // Queue params
  let queue
  let currentView
  let title

  // Task params
  let initialised = false
  let task
  let currentCount
  let deferredCount

  const resetTaskState = function () {
    initialised = false
    task = undefined
    currentCount = undefined
    deferredCount = undefined
  }

  const loadTask = function () {
    const url = '/api/tasks'
    const params = {
      'queue_id': queueId,
      'sort': '-starred,queued,id',
      'limit': '1',
    }
    return authStore.request({method: 'get', url, params})
      .then(response => {
        const taskData = response.data.tasks[0]
        if (taskData) {
          task = taskData
          currentCount = response.data.paging.total
          initialised = true
        } else {
          const url = '/api/tasks'
          const params = {
            'queue_id': queueId,
            'deferred': 'only',
            'sort': '-starred,queued,id',
            'limit': '0',
          }
          return authStore.request({method: 'get', url, params})
            .then(response => {
              currentCount = 0
              deferredCount = response.data.paging.total
              initialised = true
            })
        }
      })
      .catch(e => {
        toastStore.error('Failed to load task', e)
      })
  }

  const loadQueue = function (user, queueId) {
    // Reset queue params
    queue = undefined
    currentView = undefined
    title = undefined

    // Reset task params
    resetTaskState()

    if (user) {
      queue = user.queues.find(q => q.queue_id === queueId)
      if (queue) {
        currentView = 'queues.' + queueId
        title = queue.name
        loadTask()
      } else {
        // If the user doesn't have this queue, redirect to queue list.
        location.hash = ''
      }
    }
  }

  $: loadQueue($userStore, queueId)

  const queueMoreMenuItems = [
    {type: 'button', text: 'Current Tasks', func: () => listTasks()},
    {type: 'button', text: 'Old Tasks', func: () => listOldTasks()},
    {type: 'button', text: 'Rename Queue', func: () => editQueue()},
  ]

  let showDeferDialog = false
  const deferDialogItems = [
    {type: 'button', text: '1 hour', func: () => deferTask(1, 'hour')},
    {type: 'button', text: '6 hours', func: () => deferTask(6, 'hours')},
    {type: 'button', text: '12 hours', func: () => deferTask(12, 'hours')},
    {type: 'button', text: '24 hours', func: () => deferTask(24, 'hours')},
    {type: 'button', text: '7 days', func: () => deferTask(7, 'days')},
  ]
  const openDeferDialog = function () {
    showDeferDialog = true
  }

  let showMoveDialog = false
  let moveDialogItems = []
  const openMoveDialog = function () {
    showMoveDialog = true
  }
  const setMoveDialogItems = function (user, queueId) {
    if (user !== undefined) {
      moveDialogItems = user.queues
        .filter(q => q.queue_id !== queueId)
        .filter(q => q.status === 'current')
        .map(q => {
          return {type: 'button', text: q.name, func: () => moveTask(q.queue_id)}
        })
    }
  }
  $: setMoveDialogItems($userStore, queueId)

  let taskMoreMenuItems = []
  const setTaskMoreMenuItems = function (currentCount, moveDialogItems) {
    taskMoreMenuItems = [
      {type: 'button', text: 'Delete', func: deleteTask},
      {type: 'button', text: 'Edit', func: editTask},
    ]
    if (currentCount && currentCount > 1) {
      taskMoreMenuItems = [
        ...taskMoreMenuItems,
        {type: 'button', text: 'Bury', func: buryTask},
      ]
    }
    if (moveDialogItems.length) {
      taskMoreMenuItems = [
        ...taskMoreMenuItems,
        {type: 'button', text: 'Move', func: openMoveDialog},
      ]
    }
  }
  $: setTaskMoreMenuItems(currentCount, moveDialogItems)

  const starTask = function () {
    const taskId = task.task_id

    // No need to wait_for, or reload later. Starring a task can't reduce its priority, so if its
    // already the current task (it's being shown) it'll still be the current task after starring.
    const url = '/api/tasks/' + taskId
    const data = {
      'starred': true,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => {
        // Since we aren't reloading, need to update the current task directly.
        task.starred = true
      })
      .catch(e => toastStore.error('Failed to star task', e))
  }

  const unStarTask = function () {
    const taskId = task.task_id

    const url = '/api/tasks/' + taskId + '?wait_for'
    const data = {
      'starred': false,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => toastStore.success('Un-starred', () => reStarTask(taskId)))
      .catch(e => toastStore.error('Failed to un-star task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const reStarTask = function (taskId) {
    const url = '/api/tasks/' + taskId + '?wait_for'
    const data = {
      'starred': true,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => toastStore.success('Re-starred'))
      .catch(e => toastStore.error('Failed to re-star task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const alertTask = function (event) {
    const taskId = task.task_id

    const url = '/api/tasks/' + taskId
    const data = {
      'alert_dt': event.detail.alertDt,
    }
    return authStore.request({method: 'put', url, data})
      .catch(e => toastStore.error('Failed to set alert', e))
      .then(() => {
        // Since we aren't reloading, need to update the current task directly.
        task.alert_dt = data.alert_dt
      })
  }

  const unAlertTask = function () {
    const taskId = task.task_id

    const url = '/api/tasks/' + taskId
    const data = {
      'alert_dt': null,
    }
    return authStore.request({method: 'put', url, data})
      .catch(e => toastStore.error('Failed to remove alert', e))
      .then(() => {
        // Since we aren't reloading, need to update the current task directly.
        task.alert_dt = null
      })
  }

  const deferTask = function (length, unit) {
    const taskId = task.task_id
    const oldActiveDt = task.active_dt

    const url = '/api/tasks/' + taskId + '?wait_for'
    const data = {
      'active_dt': dayjs().add(length, unit).format(),
    }
    return authStore.request({method: 'put', url, data})
      .then(() => toastStore.success('Deferred', () => unDeferTask(taskId, oldActiveDt)))
      .catch(e => toastStore.error('Failed to defer task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const unDeferTask = function (taskId, oldActiveDt) {
    const url = '/api/tasks/' + taskId + '?wait_for'
    const data = {
      'active_dt': oldActiveDt,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => toastStore.success('Un-deferred'))
      .catch(e => toastStore.error('Failed to un-defer task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const buryTask = function () {
    const taskId = task.task_id
    const oldQueueDt = task.queue_dt
    const oldStarred = task.starred

    const url = '/api/tasks/' + taskId + '?wait_for'
    const data = {
      'queue_dt': dayjs().format(),
      'starred': false,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => toastStore.success('Buried', () => unBuryTask(taskId, oldQueueDt, oldStarred)))
      .catch(e => toastStore.error('Failed to bury task', e))
      .then(() => {
        resetTaskState()
        // Refresh "now" to make sure the task doesn't look like it was queued in the future.
        approxNowStore.refresh()
        return loadTask()
      })
  }

  const unBuryTask = function (taskId, oldQueueDt, oldStarred) {
    const url = '/api/tasks/' + taskId + '?wait_for'
    const data = {
      'queue_dt': oldQueueDt,
      'starred': oldStarred,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => toastStore.success('Dug up'))
      .catch(e => toastStore.error('Failed to dig up task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const moveTask = function (newQueueId) {
    const taskId = task.task_id
    const oldQueueId = task.queue_id

    const url = '/api/tasks/' + taskId + '?wait_for'
    const data = {
      'queue_id': newQueueId,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => toastStore.success('Moved', () => undoMoveTask(taskId, oldQueueId)))
      .catch(e => toastStore.error('Failed to move task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const undoMoveTask = function (taskId, oldQueueId) {
    const url = '/api/tasks/' + taskId + '?wait_for'
    const data = {
      'queue_id': oldQueueId,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => toastStore.success('Moved back'))
      .catch(e => toastStore.error('Failed to move task back', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const completeTask = function () {
    const taskId = task.task_id

    const url = '/api/tasks/' + taskId + '/complete?wait_for'
    return authStore.request({method: 'post', url})
      .then(() => toastStore.success('Completed', () => restoreTask(taskId)))
      .catch(e => toastStore.error('Failed to complete task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const deleteTask = function () {
    const taskId = task.task_id

    const url = '/api/tasks/' + taskId + '/delete?wait_for'
    return authStore.request({method: 'post', url})
      .then(() => toastStore.success('Deleted', () => restoreTask(taskId)))
      .catch(e => toastStore.error('Failed to delete task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const restoreTask = function (taskId) {
    const url = '/api/tasks/' + taskId + '/queue?wait_for'
    return authStore.request({method: 'post', url})
      .then(() => toastStore.success('Re-queued'))
      .catch(e => toastStore.error('Failed to re-queue task', e))
      .then(() => {
        resetTaskState()
        return loadTask()
      })
  }

  const deleteQueue = function () {
    const url = '/api/user/queues/' + queueId + '/delete'
    return authStore.request({method: 'post', url})
      .then(() => userStore.refresh())
      .then(() => {
        toastStore.success('Deleted', () => restoreQueue(queueId))
        location.hash = ''
      })
      .catch(e => toastStore.error('Failed to delete queue', e))
  }

  const restoreQueue = function (queueId) {
    const url = '/api/user/queues/' + queueId + '/restore'
    return authStore.request({method: 'post', url})
      .then(() => userStore.refresh())
      .then(() => toastStore.success('Restored'))
      .catch(e => toastStore.error('Failed to restore queue', e))
  }

  onMount(() => {
    userStore.maybeInit()
  })
</script>

{#if subView === 'queueEdit'}
  <QueueEdit queueId="{queueId}" on:back={closeSubView} on:updated={queueUpdated} />
{:else if subView === 'taskAdd'}
  <TaskAdd {queueId} on:back={closeSubView} on:added={taskAdded} />
{:else if subView === 'taskEdit'}
  <TaskEdit taskId={task.task_id} on:back={closeSubView} on:updated={taskUpdated} />
{:else}
  {#if listMode}
    <TaskList {queueId} mode={listMode} on:back={closeList} />
  {:else}
    <PageMain {currentView} {title} moreMenuItems={queueMoreMenuItems}>
      {#if !initialised}
        <div class="noticeWrapper">
          <div class="notice">Loading...</div>
        </div>
      {:else if task === undefined}
        <div class="noticeWrapper">
          <div class="notice">
            {#if deferredCount}
              <p>No current tasks.</p>
              <p>
                Add tasks below, or view
                {deferredCount} deferred {deferredCount === 1 ? 'task' : 'tasks'}.
              </p>
              <p><button class="smallButton" on:click={listTasks}>View Deferred</button></p>
            {:else}
              <p>No current tasks.</p>
              <p>Add tasks below, or delete this queue if it's no longer required.</p>
              <p><button class="smallButton" on:click={deleteQueue}>Delete Queue</button></p>
            {/if}
          </div>
        </div>
      {:else}
        <div class="taskInfo">
          Queued {dayjs(task.queue_dt).from($approxNowStore)}
          {#if $userStore && $userStore.alertsEnabled}
            {#if task.alert_dt && dayjs(task.alert_dt) > $approxNowStore}
              <br>Alerts {dayjs(task.alert_dt).from($approxNowStore)}
            {/if}
          {/if}
        </div>
        <div class="taskWrapper scrollBlock">
          <div class="spacer"></div>
          <div class="task markdown">{@html markdown(task.body)}</div>
          <div class="spacer"></div>
        </div>
        <DialogMenu bind:show={showDeferDialog} header="Defer For" menuItems={deferDialogItems} />
        {#if moveDialogItems.length}
          <DialogMenu bind:show={showMoveDialog} header="Move To" menuItems={moveDialogItems} />
        {/if}
      {/if}
      <div slot="controls" class="controls">
        {#if initialised && task !== undefined}
          <div class="toolbar">
            <StarButton starred={task.starred} on:star={starTask} on:unStar={unStarTask} />
            <AlertButton alertDt={task.alert_dt} on:alert={alertTask} on:unAlert={unAlertTask} />
            <button class="iconButton" on:click={openDeferDialog} title="Defer Task">
              <svg class="icon" width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z">
                </path>
              </svg>
            </button>
            {#if taskMoreMenuItems.length}
              <MoreMenu menuItems={taskMoreMenuItems} position="aboveRight" />
            {/if}
            <div class="spacer"></div>
            <button class="greyButton" on:click={completeTask}>Done</button>
          </div>
        {/if}
        <button class="mainButton" on:click={addTask}>Add Task</button>
      </div>
    </PageMain>
  {/if}
{/if}

<style>
  .smallButton {
    font-size: 0.8em;
  }

  .taskInfo {
    padding: 0 20px 10px;

    font-size: 0.8em;
    font-weight: 400;
  }

  :global(.taskWrapper) {
    display: flex;
    flex-direction: column;

    background-color: white;
    border-top: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);
  }
  :global(form.taskWrapper) {
    flex-grow: 1;
  }

  :global(div.task) {
    overflow-wrap: anywhere;
  }
  :global(textarea.task) {
    flex-grow: 1;

    resize: none;

    padding: 20px;

    border: none;
    border-radius: 0;
    box-shadow: none;

    font-size: 1.1em;
  }
  .task {
    align-self: center;

    padding: 20px;

    font-size: 1.3em;
  }

  .markdown :global(*:not(li)) {
    margin: 20px 0;
  }
  .markdown :global(*:is(h1, h2, h3, h4, h5, h6)) {
    margin: 30px 0 20px;
  }
  .markdown :global(*:first-child) {
    margin-top: 0;
  }
  .markdown :global(*:last-child) {
    margin-bottom: 0;
  }
</style>